import { useMemo } from "react";
import { Flex, ThemeIcon, Text, useMantineTheme } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { IconFileText } from "@tabler/icons-react";

type SharedDocument = {
  id: string;
  sharedAt: Date | string;
  downloadCount: number;
  file: {
    id: string;
    filename: string;
    name: string | null;
    mimeType: string | null;
    size: number;
  };
  sharedBy: {
    id: string;
    legalName: string | null;
    gtid: string;
  };
};

interface CollaborationDocumentsTabProps {
  sharedDocuments: SharedDocument[];
}

export default function CollaborationDocumentsTab({ sharedDocuments }: CollaborationDocumentsTabProps) {
  const theme = useMantineTheme();

  const columns: MRT_ColumnDef<SharedDocument>[] = useMemo(
    () => [
      {
        accessorFn: (row) => row.file.name || row.file.filename,
        id: "document",
        header: "Document",
        size: 250,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return (
            <Flex align="center" gap="sm">
              <ThemeIcon size={36} radius="md" color="primary" variant="light">
                <IconFileText size={18} />
              </ThemeIcon>
              <Text fz="sm">{value}</Text>
            </Flex>
          );
        },
      },
      {
        accessorFn: (row) => row.sharedBy.legalName || row.sharedBy.gtid,
        id: "sharedBy",
        header: "Shared By",
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return value || "—";
        },
      },
      {
        accessorKey: "sharedAt",
        header: "Shared On",
        size: 180,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          if (!value) return <span style={{ color: "#94a3b8" }}>—</span>;
          return new Date(value).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        },
      },
    ],
    []
  );

  return (
    <Flex direction="column" w="100%">
      <MantineReactTable
        columns={columns}
        data={sharedDocuments}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        enableRowActions={false}
        enableSorting={false}
        enableColumnFilters={false}
        initialState={{
          density: "md",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 250,
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 500,
            fontSize: "var(--mantine-font-size-md)",
            color: theme.colors.slate[6],
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "400px",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 1} style={{ textAlign: "center", padding: "2rem" }}>
              No documents have been shared in this collaboration yet.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
